import { PAGINGATION_DEFAULT } from '@const/pagination'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchList, fetchSingle, fetchCodeAndNameList } from './asyncAction'

const initialState: TAppBannerManagement.TState = {
  bannerList: {
    data: [],
    ...PAGINGATION_DEFAULT
  },
  isFetchingList: false,
  bannerUpsert: {
    data: {
      id: '',
      name: '',
      imageUrl: '',
      appScreenCode: '',
      videoUrl: '',
      displayOrder: 0,
      startDate: null,
      endDate: null,
      destinationUrl: '',
      image: null,
      video: null
    },
    errors: [
      { key: 'name', message: '' },
      { key: 'image', message: '' },
      { key: 'displayOrder', message: '' },
      { key: 'startDate', message: '' },
      { key: 'endDate', message: '' },
      { key: 'destinationUrl', message: '' },
      { key: 'video', message: '' }
    ]
  },
  isLoading: false,
  imageFile: null,
  videoFile: null,
  pageSetUpCodeAndNameList: {
    data: []
  }
}
const appBannerManagementSlice = createSlice({
  name: 'appBannerManagement',
  initialState,
  reducers: {
    changeStartDate (state, action) {
      if (action.payload) {
        state.bannerUpsert.data.startDate = action.payload
      } else {
        state.bannerUpsert.data.startDate = null
      }
    },
    changeEndDate (state, action) {
      if (action.payload) {
        state.bannerUpsert.data.endDate = action.payload
      } else {
        state.bannerUpsert.data.endDate = null
      }
    },
    changeName (state, action) {
      state.bannerUpsert.data.name = action.payload
    },
    changeDisplayOrder (state, action) {
      state.bannerUpsert.data.displayOrder = action.payload
    },
    addError (state, action) {
      const { key, message } = action.payload
      const errors = state.bannerUpsert.errors
      const updatedError = errors.filter(x => x.key === key)[0]
      if (updatedError) {
        updatedError.message = message
      }
    },
    setIsLoading (state, action) {
      state.isLoading = action.payload
    },
    resetFormValue () {
      return initialState
    },
    changeDestinationUrl (state, action) {
      state.bannerUpsert.data.destinationUrl = action.payload
    },
    changeImagePath (state, action) {
      state.bannerUpsert.data.imageUrl = action.payload
    },
    changeVideoPath (state, action) {
      state.bannerUpsert.data.videoUrl = action.payload
    },
    setImageFile (state, action: PayloadAction<File>) {
      const imageFile = action.payload
      if (imageFile) {
        state.imageFile = imageFile
      }
    },
    setVideoFile (state, action: PayloadAction<File>) {
      const videoFile = action.payload
      if (videoFile) {
        state.videoFile = videoFile
      }
    },
    removeVideo (state) {
      state.bannerUpsert.data.video = {
        originalName: '',
        fileSize: 0,
        presignedUrlKey: ''
      }
      state.videoFile = undefined
      state.bannerUpsert.data.videoUrl = ''
    },
    changeAppScreenCode (state, action) {
      state.bannerUpsert.data.appScreenCode = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchList.fulfilled, (state, action: PayloadAction<TAppBannerManagement.TState['bannerList']>) => {
        if (!action.payload?.data) return
        state.bannerList = {
          ...action.payload
        }
        state.isFetchingList = false
      })
      .addCase(fetchList.pending, state => {
        state.isFetchingList = true
      })
      .addCase(fetchSingle.fulfilled, (state, action: PayloadAction<TAppBannerManagement.TState['bannerUpsert']>) => {
        state.isLoading = false
        if (!action.payload?.data) return
        if (action.payload.data.endDate === 'Invalid date') {
          action.payload.data.endDate = null
        }
        state.bannerUpsert.data = action.payload.data
        state.bannerUpsert.errors = initialState.bannerUpsert.errors
      })
      .addCase(fetchSingle.pending, state => {
        state.isLoading = true
      })

    builder
      .addCase(fetchCodeAndNameList.fulfilled, (state, action: PayloadAction<TAppBannerManagement.TState['pageSetUpCodeAndNameList']>) => {
        state.pageSetUpCodeAndNameList = action.payload
      })
  }
})

export * from './asyncAction'

export const {
  changeStartDate,
  changeEndDate,
  addError,
  setIsLoading,
  resetFormValue,
  changeDestinationUrl,
  changeName,
  changeDisplayOrder,
  changeImagePath,
  changeVideoPath,
  setImageFile,
  setVideoFile,
  removeVideo,
  changeAppScreenCode
} = appBannerManagementSlice.actions

export const appBannerManagementReducer = appBannerManagementSlice.reducer
